import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PhaseCard = ({ phase, team, onUpdate, userRole, onNavigateToPhase }) => {
  const [isEditingSuggested, setIsEditingSuggested] = useState(false);
  const [isEditingActual, setIsEditingActual] = useState(false);
  const [isEditingAssignment, setIsEditingAssignment] = useState(false);
  const [editedSuggestedStart, setEditedSuggestedStart] = useState(phase?.suggestedStartDate || '');
  const [editedSuggestedEnd, setEditedSuggestedEnd] = useState(phase?.suggestedEndDate || '');
  const [editedActualStart, setEditedActualStart] = useState(phase?.actualStartDate || '');
  const [editedActualEnd, setEditedActualEnd] = useState(phase?.actualEndDate || '');
  const [selectedSubcontractors, setSelectedSubcontractors] = useState(phase?.assignedTo || []);

  const isBoss = userRole === 'boss';
  const isAssignedToUser = phase?.assignedTo?.some(id => 
    team?.find(member => member?.id === id && member?.role === userRole)
  );

  // Check if this is the "3 Musketeers" phase
  const isThreeMusketeersPhase = phase?.name === '3 Musketeers' || phase?.name === '3 Musketeers Installation';

  // Get individual trade progress for 3 Musketeers phase
  const getTradeProgress = (tradeName) => {
    if (!isThreeMusketeersPhase || !phase?.tradeProgress) return 0;
    return phase?.tradeProgress?.[tradeName] || 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusColor = () => {
    if (phase?.status === 'completed') return 'bg-success/10 text-success border-success/20';
    if (phase?.hasAlert) return 'bg-error/10 text-error border-error/20';
    if (phase?.status === 'in_progress') return 'bg-primary/10 text-primary border-primary/20';
    return 'bg-muted text-muted-foreground border-border';
  };

  const getProgressPercentage = () => {
    if (phase?.status === 'completed') return 100;
    if (phase?.status === 'in_progress') return 50;
    return 0;
  };

  const handleSaveSuggestedDates = () => {
    onUpdate({
      ...phase,
      suggestedStartDate: editedSuggestedStart,
      suggestedEndDate: editedSuggestedEnd
    });
    setIsEditingSuggested(false);
  };

  const handleSaveActualDates = () => {
    onUpdate({
      ...phase,
      actualStartDate: editedActualStart,
      actualEndDate: editedActualEnd,
      status: editedActualEnd ? 'completed' : 'in_progress'
    });
    setIsEditingActual(false);
  };

  const handleSaveAssignment = () => {
    onUpdate({
      ...phase,
      assignedTo: selectedSubcontractors
    });
    setIsEditingAssignment(false);
  };

  const getAssignedMembers = () => {
    return team?.filter(member => phase?.assignedTo?.includes(member?.id));
  };

  const teamOptions = team?.map(member => ({
    value: member?.id,
    label: `${member?.name} (${member?.role})`,
    description: member?.contact
  }));

  return (
    <div className={`bg-card border-2 rounded-lg p-6 hover:shadow-soft transition-smooth ${getStatusColor()}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-foreground">{phase?.name}</h3>
            {phase?.hasAlert && (
              <div className="flex items-center gap-1 px-2 py-1 bg-error/10 text-error rounded text-xs font-medium">
                <Icon name="AlertTriangle" size={14} strokeWidth={2} />
                Delayed
              </div>
            )}
            {phase?.status === 'completed' && (
              <div className="flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded text-xs font-medium">
                <Icon name="CheckCircle2" size={14} strokeWidth={2} />
                Completed
              </div>
            )}
          </div>
          
          {isThreeMusketeersPhase ? (
            <div className="space-y-2 mb-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-foreground">Plumber</span>
                  <span className="text-xs text-muted-foreground">{getTradeProgress('plumber')}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="h-2 rounded-full bg-blue-500 transition-all"
                    style={{ width: `${getTradeProgress('plumber')}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-foreground">Electrician</span>
                  <span className="text-xs text-muted-foreground">{getTradeProgress('electrical')}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="h-2 rounded-full bg-yellow-500 transition-all"
                    style={{ width: `${getTradeProgress('electrical')}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-foreground">Aircon</span>
                  <span className="text-xs text-muted-foreground">{getTradeProgress('aircon')}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="h-2 rounded-full bg-green-500 transition-all"
                    style={{ width: `${getTradeProgress('aircon')}%` }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full bg-muted rounded-full h-2 mb-3">
              <div 
                className={`h-2 rounded-full transition-all ${
                  phase?.status === 'completed' ? 'bg-success' : 
                  phase?.hasAlert ? 'bg-error' : 'bg-primary'
                }`}
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="ChevronRight"
          onClick={() => onNavigateToPhase(phase?.id)}
        />
      </div>
      <div className="space-y-4">
        <div className="bg-background/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="Calendar" size={16} strokeWidth={2} />
              Suggested Timeline
            </h4>
            {isBoss && !isEditingSuggested && (
              <button
                onClick={() => setIsEditingSuggested(true)}
                className="flex items-center justify-center w-6 h-6 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
                aria-label="Edit suggested dates"
              >
                <Icon name="Pencil" size={14} strokeWidth={2} />
              </button>
            )}
          </div>
          {isEditingSuggested && isBoss ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Start Date"
                  type="date"
                  value={editedSuggestedStart}
                  onChange={(e) => setEditedSuggestedStart(e?.target?.value)}
                />
                <Input
                  label="End Date"
                  type="date"
                  value={editedSuggestedEnd}
                  onChange={(e) => setEditedSuggestedEnd(e?.target?.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="xs"
                  iconName="Check"
                  onClick={handleSaveSuggestedDates}
                >
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  iconName="X"
                  onClick={() => {
                    setEditedSuggestedStart(phase?.suggestedStartDate || '');
                    setEditedSuggestedEnd(phase?.suggestedEndDate || '');
                    setIsEditingSuggested(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{formatDate(phase?.suggestedStartDate)}</span>
              <Icon name="ArrowRight" size={14} strokeWidth={2} />
              <span>{formatDate(phase?.suggestedEndDate)}</span>
            </div>
          )}
        </div>

        <div className="bg-background/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="Clock" size={16} strokeWidth={2} />
              Actual Timeline
            </h4>
            {(isBoss || isAssignedToUser) && !isEditingActual && (
              <button
                onClick={() => setIsEditingActual(true)}
                className="flex items-center justify-center w-6 h-6 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
                aria-label="Edit actual dates"
              >
                <Icon name="Pencil" size={14} strokeWidth={2} />
              </button>
            )}
          </div>
          {isEditingActual && (isBoss || isAssignedToUser) ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Start Date"
                  type="date"
                  value={editedActualStart}
                  onChange={(e) => setEditedActualStart(e?.target?.value)}
                />
                <Input
                  label="End Date"
                  type="date"
                  value={editedActualEnd}
                  onChange={(e) => setEditedActualEnd(e?.target?.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="xs"
                  iconName="Check"
                  onClick={handleSaveActualDates}
                >
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  iconName="X"
                  onClick={() => {
                    setEditedActualStart(phase?.actualStartDate || '');
                    setEditedActualEnd(phase?.actualEndDate || '');
                    setIsEditingActual(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{formatDate(phase?.actualStartDate)}</span>
              <Icon name="ArrowRight" size={14} strokeWidth={2} />
              <span>{formatDate(phase?.actualEndDate)}</span>
            </div>
          )}
        </div>

        <div className="bg-background/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="Users" size={16} strokeWidth={2} />
              Assigned To
            </h4>
            {isBoss && !isEditingAssignment && (
              <button
                onClick={() => setIsEditingAssignment(true)}
                className="flex items-center justify-center w-6 h-6 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
                aria-label="Edit assignments"
              >
                <Icon name="Pencil" size={14} strokeWidth={2} />
              </button>
            )}
          </div>
          {isEditingAssignment && isBoss ? (
            <div className="space-y-3">
              <Select
                options={teamOptions}
                value={selectedSubcontractors}
                onChange={setSelectedSubcontractors}
                multiple
                searchable
                placeholder="Select team members"
              />
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="xs"
                  iconName="Check"
                  onClick={handleSaveAssignment}
                >
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  iconName="X"
                  onClick={() => {
                    setSelectedSubcontractors(phase?.assignedTo || []);
                    setIsEditingAssignment(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {getAssignedMembers()?.length > 0 ? (
                getAssignedMembers()?.map(member => (
                  <div key={member?.id} className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-medium">
                    <span>{member?.name}</span>
                    <span className="text-primary/60">({member?.role})</span>
                  </div>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">No assignments</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhaseCard;